import { PermissionModel, UserActions } from "@granular/authz";
import type { RPCRouter } from "@granular/fabric3-server";
import { UserResponse } from "@granular/user-svc-client";
import type { inferRouterOutputs } from "@trpc/server";
import { useAtom, useSetAtom } from "jotai";
import { RESET } from "jotai/utils";
import React, { useMemo } from "react";
import { userCanContext } from "../helpers/permissions";
import { coreQueryClient } from "../services/trpc";
import { contextStringPersisted, fullContext } from "../state/local";
import { findSelectedContext } from "./resolveDeepLinkContext";

type RouterOutput = inferRouterOutputs<RPCRouter>;

export type ContextResponse = RouterOutput["bizContext"]["getContext"];
export type SingleContext = RouterOutput["bizContext"]["getContext"][number];

/**
 * This hook is used to get the Context/Agency data from the API.
 * @returns
 * - allContexts: All the contexts/agency data from the API.
 * - contextSelected: The context/agency selected by the user. The selected context is stored by the app name:
 * - setContextSelected: Function to set the context/agency selected according to the current app.
 * - isLoading: always false due to the useSuspenseQuery.
 * - isError: always false due to the useSuspenseQuery.
 */
export function useOperatingContext() {
  // For the root context we use the "default" key to store the selected context.
  /**
   * Context is a mandatory information for the entire ecosystem, so we use suspense query here
   * instead of the useQuery to ensure that Context data will be loaded before rendering anything.
   * With SuspenseQuery, the data will never return undefined, and the fallback
   * Loading component (from Container) will be shown until the data is loaded.
   */
  const [allContexts, { isError, isPending: isLoading }] =
    coreQueryClient.bizContext.getContext.useSuspenseQuery(undefined, {
      staleTime: 60 * 1000 * 60 * 24,
    });

  const bestDefault = useMemo(() => allContexts?.[0], [allContexts]);

  const setPersistedContext = useSetAtom(contextStringPersisted);
  const [context, setContext] = useAtom(fullContext);
  /**
   * Check if the context passed as parameter is a valid context.
   * To be valid the context must exists in the allContexts array.
   * @returns a boolean indicating if the context is valid.
   */
  const isValidContext = React.useCallback(
    (context: SingleContext | undefined) => {
      if (!context) return false;
      return allContexts?.some(
        ({ contextId }) => contextId === context.contextId,
      );
    },
    [allContexts],
  );

  /**
   * Set the global context according to the new context selected.
   * The context selected will be the first valid context found in the following order:
   * 1. The context selected by the user (obviously, if it's valid)
   * 2. The current context (nothing will change)
   * 3. The first context in the allContexts array (fallback)
   * @returns the new context selected or undefined if no valid context was found (if user don't have any context)
   */
  const setValidContextSelected = React.useCallback(
    (newContext: SingleContext | undefined) => {
      try {
        // this can be undefined if the user doesn't have any context
        const validContext: SingleContext | undefined = isValidContext(
          newContext,
        )
          ? newContext
          : (bestDefault ?? allContexts?.[0]);

        // Native mobile code
        if (validContext) {
          // Android Code
          globalThis.Mobile?.onRoleChanged(validContext.contextId);

          // iOS code
          globalThis.webkit?.messageHandlers?.onRoleChanged?.postMessage({
            contextId: validContext.contextId,
          });
        }
        if (validContext) {
          setContext(validContext.contextId);
        }
      } catch (error) {
        console.log(`Unable to set context selected: ${JSON.stringify(error)}`);
      }
    },
    [allContexts, isValidContext, bestDefault, setContext],
  );

  // TODO: Fix manual memoization error

  const switchedSelectedContext = useMemo(
    () => findSelectedContext(allContexts, context?.fullString),
    [allContexts, context?.fullString],
  );

  /**
   * Checks where or not the user is allowed to perform the given action for the
   * given model for the currently selected context
   *
   * @param action - The user action defined in authz
   * @param model - The model to verify the user permissions for this action
   * @returns Boolean indicating userCan
   */
  const userCan = (action: UserActions, model: PermissionModel) => {
    // This is wrapped like this so that it's easier to mock
    return userCanContext(switchedSelectedContext, action, model);
  };

  /**
   * Clear all saved context data from localStorage, and reset the Jotai State Atom
   */
  const resetLocalStoredContexts = () => {
    setPersistedContext(RESET);
  };

  // isLoading and isError are returned only for compatibility with old codebase
  return {
    allContexts,
    contextSelected: switchedSelectedContext,
    setContextSelected: setValidContextSelected,
    resetLocalStoredContexts,
    userCan,
    isLoading,
    isError,
  };
}

declare global {
  var Mobile:
    | {
        onRoleChanged: (contextData: string) => void;
        startImpersonation: (payload: string) => void;
        startImpersonationV2: (
          correlationId: string,
          preferTargetsFeatureFlags: boolean,
        ) => void;
        stopImpersonation: (payload: string) => void;
        updatePreferences: (paylad: UserResponse) => void;
      }
    | undefined;

  var webkit:
    | {
        messageHandlers?: {
          onRoleChanged?: {
            postMessage: (payload: Record<string, unknown>) => void;
          };
          startImpersonation?: {
            postMessage: (payload: string) => void;
          };
          startImpersonationV2?: {
            postMessage: (payload: {
              correlationId: string;
              preferTargetsFeatureFlags: boolean;
            }) => void;
          };
          stopImpersonation?: {
            postMessage: (payload: string) => void;
          };
          updatePreferences?: {
            postMessage: (paylad: UserResponse) => void;
          };
        };
      }
    | undefined;
}
